import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { GET_PRICING_PLAN_RESOURCE } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { useUpdatePricingPlan } from 'src/features/pricingPlan/api/useUpdatePricingPlan';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PRICING_PLAN_STATUS, PricingPlan } from 'src/features/pricingPlan/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface PublishModalProps {
    plan: PricingPlan;
    children: React.ReactNode;
}

export default function PublishPlanModal({ plan, children }: PublishModalProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const pricingPlanName =
        plan?.translations?.find((plan) => plan.language === locale)?.name ??
        formatMessage(pricingPlanMessages.pricingPlan);

    const { mutate: updatePlan, isPending: isPendingUpdate } = useUpdatePricingPlan({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [GET_PRICING_PLAN_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(pricingPlanMessages.publishPricingPlanSuccess),
                description: formatMessage(pricingPlanMessages.publishPricingPlanSuccessDescription, {
                    entry: pricingPlanName,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () =>
        updatePlan({
            planId: plan.id,
            parkingId,
            data: {
                ...plan,
                fixed_rate: plan?.fixed_rate ? { rate_with_tax: plan.fixed_rate.rate_with_tax.toString() } : undefined,
                status: PRICING_PLAN_STATUS.PUBLISHED,
            },
        });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.PUBLISH} />
                        <DialogTitle>
                            <FormattedMessage
                                id={pricingPlanMessages.publishPlan.id}
                                defaultMessage={pricingPlanMessages.publishPlan.defaultMessage}
                                values={{ entry: pricingPlanName }}
                            />
                        </DialogTitle>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={pricingPlanMessages.confirmPublishPlan.id}
                            defaultMessage={pricingPlanMessages.confirmPublishPlan.defaultMessage}
                            values={{ entry: pricingPlanName }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button disabled={isPendingUpdate} type="submit" onClick={onSubmit} className="flex-1">
                        {isPendingUpdate && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage {...pricingPlanMessages.publish} />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
